import { SortChevronIcon } from '@src/svgs/SortChevronIcon';
import styled from 'styled-components';

import type { FC } from 'react';

export type SortKey = 'name' | 'title' | 'createdBy';
export type SortDir = 'asc' | 'desc';

type Props = {
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  showAiVoices?: boolean;
};

/** Column header row for the Speaker Manager list. Mirrors StoryListHeader. */
const SpeakerListHeader: FC<Props> = ({
  sortKey,
  sortDir,
  onSort,
  showAiVoices = false,
}) => (
  <Row data-testid="speaker-list-header">
    <Left>
      <ExpandSpacer />
      <NameHeader
        type="button"
        onClick={() => onSort('name')}
        $active={sortKey === 'name'}
        data-testid="speaker-sort-name"
      >
        Name
        {sortKey === 'name' && <SortIcon $desc={sortDir === 'desc'} />}
      </NameHeader>
    </Left>
    <Right>
      <TitleHeader
        type="button"
        onClick={() => onSort('title')}
        $active={sortKey === 'title'}
        data-testid="speaker-sort-title"
      >
        Title
        {sortKey === 'title' && <SortIcon $desc={sortDir === 'desc'} />}
      </TitleHeader>
      <CreatedByHeader
        type="button"
        onClick={() => onSort('createdBy')}
        $active={sortKey === 'createdBy'}
        data-testid="speaker-sort-created-by"
      >
        Created By
        {sortKey === 'createdBy' && <SortIcon $desc={sortDir === 'desc'} />}
      </CreatedByHeader>
      {showAiVoices && (
        <NumericHeader title="AI Voices mapped to this speaker">
          AI Voices
        </NumericHeader>
      )}
      <StoriesHeader title="Used in stories">Stories</StoriesHeader>
      <ActionsSpacer />
    </Right>
  </Row>
);

export default SpeakerListHeader;

const SortIcon: FC<{ $desc: boolean }> = ({ $desc }) => (
  <Caret aria-hidden="true" $desc={$desc}>
    <SortChevronIcon />
  </Caret>
);

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  border-bottom: 1px solid var(--border);
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 0 0 280px;
  min-width: 0;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex: 1;
  min-width: 0;
`;

const ExpandSpacer = styled.span`
  width: 28px;
  flex-shrink: 0;
`;

const HeaderButton = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: ${({ $active }) =>
    $active ? 'var(--font-weight-medium)' : 'var(--font-weight-regular)'};
  color: ${({ $active }) =>
    $active ? 'var(--foreground)' : 'var(--text-muted)'};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: var(--foreground);
  }
`;

const NameHeader = styled(HeaderButton)`
  text-align: left;
`;

const TitleHeader = styled(HeaderButton)`
  flex: 1;
  min-width: 0;
  justify-content: flex-start;
`;

const CreatedByHeader = styled(HeaderButton)`
  width: 140px;
  white-space: nowrap;
  justify-content: flex-start;
`;

const StoriesHeader = styled.span`
  width: 90px;
  text-align: right;
  white-space: nowrap;
`;

const NumericHeader = styled.span`
  width: 90px;
  text-align: right;
  white-space: nowrap;
`;

const ActionsSpacer = styled.span`
  width: 32px;
`;

const Caret = styled.span<{ $desc: boolean }>`
  display: inline-flex;
  transform: ${({ $desc }) => ($desc ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 120ms ease;
`;
